import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { fetchCampaignItems, updateCampaign, updateCampaignItem } from 'api/campaigns';
import {
    FundraisingSettingsForm,
    RegistrationFundraisingType,
    RegistrationFundraisingValue,
    TicketUpdateItem,
} from 'assets/js/pages/campaign-settings/campaign-team/types';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { SelectItemWithLabel, SelectListItem, UseFormSearchableSelect } from 'components/select';
import * as SettingsCard from 'components/settings-card';
import { CAMPAIGN_TEAM_FUNDRAISING_SETTINGS_LINK } from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { TransformedCampaign, useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaignItems } from 'customHooks/useInfiniteCampaignItems';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItem } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const CampaignTeamFundraisingSettings = () => {
    const toast = useToast();
    const [searchTickets, setSearchTickets] = useState('');
    const debouncedSearchTickets = useDebounce(searchTickets);
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const { data: event } = useCurrentCampaignEvent();
    const { accountId, name } = useCurrentAccount();
    const eventId = event.id;
    const campaignId = campaign.id;

    const params = {
        scope: 'registrant',
    };

    const { data: registrantTickets } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, params),
        queryFn: () =>
            fetchCampaignItems({
                campaignId: campaign.id,
                ...params,
            }),
    });

    const { data: ticketsData, ref: ticketsRef } = useInfiniteCampaignItems({
        query: debouncedSearchTickets,
    });

    const tickets = ticketsData?.pages ?? [];
    const registrantTicketsData = registrantTickets?.data ?? [];

    const methods = Form.useGivebutterForm<FundraisingSettingsForm>({
        defaultValues: defaultForm(campaign, registrantTicketsData),
    });
    const { control, path, handleSubmit, setValue, watch, resetField } = methods;

    const {
        hasGoalMinimum,
        customButtonText,
        registrationEnabled,
        registrationType,
        hasDescription,
        hasThankYouMessage,
    } = watch();

    const { mutateAsync: submit } = useMutation({
        mutationFn: (values: FundraisingSettingsForm) => {
            const buttonText = values.customButtonText ? values.buttonText : '';

            const settings = [
                {
                    code: Settings.DisableTeamFundraising.Code,
                    active: !values.enabled,
                },
                {
                    code: Settings.DisableFundraiseButton.Code,
                    active: !values.publicButton,
                },
                {
                    code: Settings.FundraiseButtonText.Code,
                    value: buttonText,
                    active: buttonText.length !== 0,
                },
                {
                    code: Settings.EnableRegistrationFundraising.Code,
                    active: values.registrationEnabled,
                    value: buildRegistrationFundraisingValue(values),
                },
                {
                    code: Settings.RegistrationThankYouMessage.Code,
                    value: values.hasThankYouMessage ? values.registrationThankYouMessage : '',
                    active:
                        values.hasThankYouMessage && values.registrationThankYouMessage.length > 0,
                },
                {
                    code: Settings.HideMemberRaised.Code,
                    active: values.hideIndividualAmount,
                },
            ];

            return updateCampaign({ campaignId: campaign?.id, data: { settings } });
        },
        onSuccess: () => {
            toast.success('Updated team fundraising settings successfully!');

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
                refetchType: 'none',
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update team fundraising settings'),
    });

    const { mutateAsync: updateTickets } = useMutation({
        mutationFn: async (ticketsToUpdate: Array<TicketUpdateItem>) =>
            Promise.all(
                ticketsToUpdate.map(({ id, data }) =>
                    updateCampaignItem({
                        campaignId: campaign.id,
                        itemId: id,
                        data,
                    }),
                ),
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.items(campaign.id),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update tickets'),
    });

    const resetRegistrationFields = () => {
        resetField('registrationType');
        resetField('tickets');
    };

    const handleFormSubmit: SubmitHandler<FundraisingSettingsForm> = async values => {
        await submit(values);
        const shouldReset = !registrationEnabled && registrantTicketsData.length > 0;

        if (shouldReset) {
            resetRegistrationFields();
        }

        const ticketsToUpdate = shouldReset
            ? registrantTicketsData.map(ticket => ({
                  id: ticket.id,
                  data: { scope: null },
              }))
            : getTicketsToUpdate(registrantTicketsData, values.tickets, {
                  scope: params.scope,
              });

        const requiresUpdate =
            shouldReset ||
            values.tickets?.length > 0 ||
            ticketsToUpdate.length > (values.tickets?.length ?? 0);

        if (requiresUpdate) {
            await updateTickets(ticketsToUpdate);
        }
    };

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>Peer-to-peer fundraising</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Configure how you'd like individuals to fundraise.
                    </p>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={CAMPAIGN_TEAM_FUNDRAISING_SETTINGS_LINK}
                        className={'text-sm'}
                    >
                        Read the guide
                    </a>
                    .
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                        <div className={'flex flex-col gap-8'}>
                            <div className={'flex flex-col gap-6'}>
                                <div className={'flex flex-col gap-2'}>
                                    <div className={'flex items-center justify-between gap-2'}>
                                        <Form.Label className={'text-base'} name={path('enabled')}>
                                            Enable peer-to-peer fundraising
                                        </Form.Label>
                                        <Form.Switch control={control} name={'enabled'} />
                                    </div>
                                    <p className={'text-sm text-gray-700'}>
                                        Fuel friendly competition and boost donor engagement by
                                        enabling P2P fundraising on your campaign.
                                    </p>
                                </div>
                                <div className={'flex flex-col gap-3'}>
                                    <div className={'flex gap-2'}>
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={'publicButton'}
                                        />
                                        <Form.Container>
                                            <Form.Label
                                                className={'mb-0.5 leading-none'}
                                                name={path('publicButton')}
                                            >
                                                Enable public "Fundraise" button
                                            </Form.Label>
                                            <p className={'text-sm text-gray-700'}>
                                                Allow anyone on your campaign page to register to
                                                fundraise by clicking the "Fundraise" button. If
                                                unchecked, you can still invite individuals to join
                                                via email or link.
                                            </p>
                                        </Form.Container>
                                    </div>
                                    <div className={'flex gap-2'}>
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={'customButtonText'}
                                        />
                                        <Form.Container>
                                            <Form.Label
                                                className={'mb-0.5 leading-none'}
                                                name={path('customButtonText')}
                                            >
                                                Custom "Fundraise" button text
                                            </Form.Label>
                                            <p className={'text-sm text-gray-700'}>
                                                Change the text displayed on the "Fundraise" button.
                                            </p>
                                        </Form.Container>
                                    </div>
                                    {customButtonText && (
                                        <div className={'w-64 pl-6'}>
                                            <Form.TextInputRoot
                                                name={'buttonText'}
                                                control={control}
                                            >
                                                <Form.TextInputForm placeholder="Fundraise" />
                                            </Form.TextInputRoot>
                                            <Form.UseFormError name={path('buttonText')} />
                                        </div>
                                    )}
                                    <div className={'flex gap-2'}>
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={'hideIndividualAmount'}
                                        />
                                        <Form.Container>
                                            <Form.Label
                                                className={'mb-0.5 leading-none'}
                                                name={path('hideIndividualAmount')}
                                            >
                                                Hide amount raised per individual
                                            </Form.Label>
                                            <p className={'text-sm text-gray-700'}>
                                                Hide the total amount raised by each individual.
                                            </p>
                                        </Form.Container>
                                    </div>
                                </div>
                                <Separator />
                                <div className={'flex flex-col gap-2'}>
                                    <div className={'flex items-center justify-between gap-1'}>
                                        <Form.Label
                                            className={'text-base'}
                                            name={path('registrationEnabled')}
                                        >
                                            Enable registration fundraising
                                        </Form.Label>
                                        <Form.Switch
                                            control={control}
                                            name={'registrationEnabled'}
                                        />
                                    </div>
                                    <p className={'text-sm text-gray-700'}>
                                        Require P2P fundraisers to register for your event through a
                                        ticket purchase or minimum donation amount.
                                    </p>
                                </div>
                                {registrationEnabled && (
                                    <div className={'flex flex-col gap-4'}>
                                        <div className={'flex flex-col gap-1'}>
                                            <div
                                                className={
                                                    'flex items-center justify-between gap-2'
                                                }
                                            >
                                                <p className={'text-sm font-medium'}>
                                                    Registration requirement
                                                </p>
                                            </div>
                                            <p className={'text-sm text-gray-700'}>
                                                Choose how fundraisers are able to register.
                                            </p>
                                        </div>
                                        <Form.UseFormRadioGroup
                                            name={'registrationType'}
                                            control={control}
                                            orientation={'vertical'}
                                            variant={'card'}
                                            defaultValue={'donation'}
                                            onChange={value => {
                                                if (value === 'donation') {
                                                    setValue(
                                                        'tickets',
                                                        [] as SelectItemWithLabel<CampaignItem>[],
                                                    );
                                                } else if (value === 'ticket') {
                                                    setValue('donationMinimum', null);
                                                }
                                            }}
                                        >
                                            <RadioGroup.Item
                                                id={'registration-fundraising-type-donation'}
                                                value={'donation'}
                                                className={'flex gap-x-3'}
                                            >
                                                <RadioGroup.IndicatorContainer className={'mt-1'}>
                                                    <RadioGroup.Indicator />
                                                </RadioGroup.IndicatorContainer>
                                                <div className={'flex flex-1 flex-col'}>
                                                    <Form.Label
                                                        className={'text-sm'}
                                                        name={path('registrationType')}
                                                        htmlFor={
                                                            'registration-fundraising-type-donation'
                                                        }
                                                    >
                                                        Donation
                                                    </Form.Label>
                                                    <p className={'text-sm text-gray-600'}>
                                                        Make a minimum donation amount.
                                                    </p>
                                                </div>
                                            </RadioGroup.Item>
                                            <RadioGroup.Item
                                                id={'registration-fundraising-type-ticket'}
                                                value={'ticket'}
                                                className={'flex gap-x-3'}
                                            >
                                                <RadioGroup.IndicatorContainer className={'mt-1'}>
                                                    <RadioGroup.Indicator />
                                                </RadioGroup.IndicatorContainer>
                                                <div className={'flex flex-1 flex-col'}>
                                                    <Form.Label
                                                        className={'text-sm'}
                                                        name={path('registrationType')}
                                                        htmlFor={
                                                            'registration-fundraising-type-ticket'
                                                        }
                                                    >
                                                        Ticket purchase
                                                    </Form.Label>
                                                    <p className={'text-sm text-gray-600'}>
                                                        Purchase a specific ticket type.
                                                    </p>
                                                </div>
                                            </RadioGroup.Item>
                                        </Form.UseFormRadioGroup>
                                    </div>
                                )}
                                {registrationEnabled && (
                                    <div className={'flex flex-col gap-4'}>
                                        {registrationType === 'ticket' && (
                                            <div className="flex flex-col gap-4">
                                                <div className="flex flex-col gap-1">
                                                    <Form.Label name={path('tickets')}>
                                                        Ticket types
                                                    </Form.Label>
                                                    <UseFormSearchableSelect
                                                        multi={true}
                                                        control={control}
                                                        name={'tickets'}
                                                        localSearch={false}
                                                        rules={{
                                                            validate: value => {
                                                                if (
                                                                    registrationType === 'ticket' &&
                                                                    (!value || value.length === 0)
                                                                ) {
                                                                    return 'Please select at least one ticket';
                                                                }
                                                                return true;
                                                            },
                                                        }}
                                                        inputProps={{
                                                            placeholder: 'Select a ticket',
                                                            onChange: e => {
                                                                setSearchTickets(
                                                                    e.currentTarget.value,
                                                                );
                                                            },
                                                        }}
                                                    >
                                                        {tickets.length === 0 && (
                                                            <div className="p-2 text-sm text-gray-500">
                                                                No tickets found
                                                            </div>
                                                        )}
                                                        {tickets.map((item: CampaignItem) => (
                                                            <SelectListItem
                                                                key={item.id}
                                                                value={{
                                                                    id: item.id.toString(),
                                                                    label: item.name,
                                                                    value: item,
                                                                }}
                                                            >
                                                                {item.name}
                                                            </SelectListItem>
                                                        ))}
                                                        <div ref={ticketsRef} />
                                                    </UseFormSearchableSelect>
                                                    <p className="text-sm text-gray-600">
                                                        {!eventId && (
                                                            <>
                                                                <Link
                                                                    to={`/accounts/${accountId}/campaigns/${campaignId}/event`}
                                                                >
                                                                    Create an event
                                                                </Link>{' '}
                                                                and add tickets for fundraisers to
                                                                purchase
                                                            </>
                                                        )}
                                                        {eventId && tickets.length === 0 && (
                                                            <>
                                                                <Link
                                                                    to={`/accounts/${accountId}/campaigns/${campaignId}/event/tickets`}
                                                                >
                                                                    Add tickets
                                                                </Link>{' '}
                                                                to your event that fundraisers are
                                                                required to purchase
                                                            </>
                                                        )}
                                                        {eventId && tickets.length > 0 && (
                                                            <>
                                                                Choose the event tickets that
                                                                fundraisers are required to purchase
                                                            </>
                                                        )}
                                                    </p>
                                                    <Form.UseFormError name={path('tickets')} />
                                                </div>
                                            </div>
                                        )}
                                        {registrationType === 'donation' && (
                                            <div className="flex flex-col gap-1">
                                                <Form.Label name={path('donationMinimum')}>
                                                    Donation minimum
                                                </Form.Label>
                                                <Form.TextInputRoot
                                                    name={'donationMinimum'}
                                                    control={control}
                                                    rules={{
                                                        validate: value => {
                                                            if (
                                                                registrationType === 'donation' &&
                                                                (!value || value === null)
                                                            ) {
                                                                return 'Donation minimum is required';
                                                            }
                                                            return true;
                                                        },
                                                    }}
                                                    inputRootProps={{
                                                        mask: 'decimal',
                                                    }}
                                                >
                                                    <InputCurrencyIcon />
                                                    <Form.TextInputForm placeholder="0.00" />
                                                </Form.TextInputRoot>
                                                <p className="text-sm text-gray-600">
                                                    Set a minimum transaction amount required for
                                                    fundraisers to join.
                                                </p>
                                                <Form.UseFormError name={path('donationMinimum')} />
                                            </div>
                                        )}
                                    </div>
                                )}
                                {registrationEnabled && <Separator />}
                                {registrationEnabled && (
                                    <div className={'flex flex-col gap-8'}>
                                        <div className={'flex flex-col gap-1'}>
                                            <div className={'flex flex-col gap-2'}>
                                                <Form.UseFormLabel name={path('suggestedAmount')}>
                                                    Suggested fundraising goal
                                                </Form.UseFormLabel>
                                                <Form.TextInputRoot
                                                    name={'suggestedAmount'}
                                                    control={control}
                                                    inputRootProps={{
                                                        mask: 'decimal',
                                                    }}
                                                >
                                                    <InputCurrencyIcon />
                                                    <Form.TextInputForm placeholder="0.00" />
                                                </Form.TextInputRoot>
                                                <Form.UseFormError name={path('suggestedAmount')} />
                                            </div>

                                            <span className="text-sm text-gray-600">
                                                Recommend a fundraising goal for P2P fundraisers
                                            </span>
                                        </div>
                                        <div className={'flex flex-col gap-3'}>
                                            <div className={'flex items-start gap-2'}>
                                                <div className="flex h-auto">
                                                    <Form.UseFormCheckboxInput
                                                        control={control}
                                                        name={path('hasGoalMinimum')}
                                                    />
                                                </div>
                                                <Form.Container>
                                                    <Form.Label
                                                        className={'mb-0.5 leading-none'}
                                                        name={path('hasGoalMinimum')}
                                                    >
                                                        Set a minimum goal for fundraisers to reach
                                                    </Form.Label>
                                                </Form.Container>
                                            </div>
                                            {hasGoalMinimum && (
                                                <div className={'w-64 pl-6'}>
                                                    <Form.TextInputRoot
                                                        name={'goalMinimum'}
                                                        control={control}
                                                        inputRootProps={{
                                                            mask: 'decimal',
                                                        }}
                                                    >
                                                        <InputCurrencyIcon />
                                                        <Form.TextInputForm placeholder="0.00" />
                                                    </Form.TextInputRoot>
                                                    <Form.UseFormError name={path('goalMinimum')} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {registrationEnabled && (
                                    <div className={'flex flex-col gap-3'}>
                                        <div className={'flex items-start gap-2'}>
                                            <div className="flex h-auto">
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={path('hasDescription')}
                                                />
                                            </div>
                                            <Form.Container>
                                                <Form.Label
                                                    className={'mb-0.5 leading-none'}
                                                    name={path('hasDescription')}
                                                >
                                                    Add preset fundraiser story
                                                </Form.Label>
                                                <p className={'text-sm text-gray-700'}>
                                                    Set a starting story for fundraisers to show on
                                                    their page.
                                                </p>
                                            </Form.Container>
                                        </div>
                                        {hasDescription && (
                                            <>
                                                <Form.Textarea
                                                    control={control}
                                                    name={'description'}
                                                    rows={7}
                                                    placeholder={`I am excited on behalf of ${name}. Join me in fundraising for ${name} so that we can help them continue to further their cause and make a difference.`}
                                                />
                                                <Form.UseFormError name={path('description')} />
                                            </>
                                        )}
                                    </div>
                                )}
                                {registrationEnabled && (
                                    <div className={'flex flex-col gap-3'}>
                                        <div className={'flex items-start gap-2'}>
                                            <div className="flex h-auto">
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={path('hasThankYouMessage')}
                                                />
                                            </div>
                                            <Form.Container>
                                                <Form.Label
                                                    className={'mb-0.5 leading-none'}
                                                    name={path('hasThankYouMessage')}
                                                >
                                                    Add preset thank you message
                                                </Form.Label>
                                                <p className={'text-sm text-gray-700'}>
                                                    Set a thank you message that fundraisers will
                                                    see after they register.
                                                </p>
                                            </Form.Container>
                                        </div>
                                        {hasThankYouMessage && (
                                            <>
                                                <Form.Textarea
                                                    control={control}
                                                    name={'registrationThankYouMessage'}
                                                    rows={7}
                                                    placeholder={`Thank you for joining me in fundraising for ${name}! We are so excited to have you on board and look forward to seeing your progress.`}
                                                />
                                                <Form.UseFormError
                                                    name={path('registrationThankYouMessage')}
                                                />
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

const getRemovedTicketIds = (
    registrantTicketsData: CampaignItem[],
    selectedTickets?: SelectItemWithLabel<CampaignItem>[],
): number[] => {
    const currentRegistrantTicketIds = registrantTicketsData.map(ticket => ticket.id);
    const selectedTicketIds = selectedTickets?.map(ticket => ticket.value?.id);

    return currentRegistrantTicketIds.filter(id => !selectedTicketIds?.includes(id));
};

const getTicketsToUpdate = (
    registrantTicketsData: CampaignItem[],
    selectedTickets: SelectItemWithLabel<CampaignItem>[],
    options: Pick<CampaignItem, 'scope'>,
): Array<TicketUpdateItem> => {
    const removedTicketIds = getRemovedTicketIds(registrantTicketsData, selectedTickets);

    return [
        ...selectedTickets.map(ticket => ({
            id: ticket.value ? ticket.value.id : -1,
            data: { scope: options.scope },
        })),
        ...removedTicketIds.map(id => ({
            id,
            data: { scope: null },
        })),
    ];
};

const buildRegistrationFundraisingValue = (
    values: FundraisingSettingsForm,
): RegistrationFundraisingValue => {
    const result: RegistrationFundraisingValue = {
        goal: {
            default: null,
        },
        minimum: maskDecimal(values.donationMinimum),
    };

    if (values.suggestedAmount !== null) {
        result.goal = {
            default: maskDecimal(values.suggestedAmount),
        };
    }

    if (result.goal && values.hasGoalMinimum && values.goalMinimum !== null) {
        result.goal.minimum = maskDecimal(values.goalMinimum);
    }

    if (values.hasDescription && values.description.length > 0) {
        result.description = {
            default: values.description,
        };
    }

    return result;
};

const determineRegistrationType = (
    campaign: TransformedCampaign,
    registrantTickets: CampaignItem[],
): RegistrationFundraisingType => {
    if (campaign?.enableRegistrationFundraising?.value?.minimum) {
        return 'donation';
    }

    if (registrantTickets.length > 0) {
        return 'ticket';
    }

    return 'donation';
};

const defaultForm = (
    campaign: TransformedCampaign,
    registrantTickets: CampaignItem[],
): Partial<FundraisingSettingsForm> => {
    const selectedTickets = registrantTickets.map(item => ({
        id: item.id.toString(),
        label: item.name,
        value: item,
    }));
    const registrationType = determineRegistrationType(campaign, registrantTickets);
    const hasDescription = Boolean(
        campaign?.enableRegistrationFundraising?.value?.description?.default,
    );
    const hasThankYouMessage = Boolean(campaign?.registrationThankYouMessage?.value);

    return {
        enabled: !campaign?.disableTeamFundraising?.active,
        publicButton: !campaign?.disableFundraiseButton?.active,
        buttonText: campaign?.fundraiseButtonText?.value ?? '',
        description: campaign?.enableRegistrationFundraising?.value?.description?.default ?? '',
        suggestedAmount: campaign?.enableRegistrationFundraising?.value?.goal?.default ?? null,
        registrationEnabled: campaign?.enableRegistrationFundraising?.active ?? false,
        donationMinimum: campaign?.enableRegistrationFundraising?.value?.minimum ?? null,
        goalMinimum: campaign?.enableRegistrationFundraising?.value?.goal?.minimum ?? null,
        registrationThankYouMessage: campaign?.registrationThankYouMessage?.value ?? '',
        tickets: selectedTickets,
        hideIndividualAmount: campaign?.hideMemberRaised?.active ?? false,
        customButtonText: Boolean(campaign?.fundraiseButtonText?.value),
        hasGoalMinimum: Boolean(campaign?.enableRegistrationFundraising?.value?.goal?.minimum),
        registrationType,
        hasDescription,
        hasThankYouMessage,
    };
};

export { CampaignTeamFundraisingSettings };
